import React, { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import '../assets/index.css';
import {
  Page,
  Text,
  Card,
  Button,
  BlockStack,
  Box,
  InlineStack,
  ProgressBar,
  Icon,
  ButtonGroup,
  IndexTable,
  LegacyCard,
  IndexFilters,
  useSetIndexFiltersMode,
  useIndexResourceState,
  ChoiceList,
  RangeSlider,
  Badge,
  useBreakpoints,
  Thumbnail,
  Toast,
  Spinner,
  Pagination,
  Tooltip,
  Modal,
  Checkbox,
  Banner,
} from "@shopify/polaris";
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExternalIcon,
  RefreshIcon,
  XIcon,
  DeleteIcon,
  EditIcon,
} from "@shopify/polaris-icons";
import { campaignAPI } from '../services/campaignAPI';
import { clientAPI } from '../services/clientAPI';
import PageTitleBar from '../components/PageTitleBar';
import { graphqlCall, formatMoney, apiCall } from '../helper/Common';
import { deleteProductMetafield, deleteCampaignCountMetafield, getProductMetafield, updateProductMetafield } from '../helper/metafieldHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFilteredCampaigns, updateCampaignStatus } from '../store/slices/campaignSlice';
import { checkInstall, updateOnboardingStatus } from '../store/slices/installSlice';
import { useSafeAppBridge } from '../hooks/useSafeAppBridge';
import { useEnsureInstall } from '../hooks/useEnsureInstall';
import { Redirect, History } from '@shopify/app-bridge/actions';
import { SHOPIFY_API_KEY, buildNodeApiEntryUrl } from '../config/env';

// Constants
const APP_BLOCK_TYPE = 'shopify://apps/lift-presales/blocks/campaign/';
const TOTAL_STEPS = 6;
const DEFAULT_GOAL_RANGE = [0, 100000];
const TAB_STRINGS = ['All', 'Active', 'Scheduled', 'Draft', 'Expired'];

/** 3-stage billing notice from install/trial start: 1-7, 8-14, then 15+. */
function computeBillingApprovalNotice(billing, nowMs) {
  if (!billing || billing.billing_status === 'active') return null;
  const trialDays = Number(billing.trial_days) || 7;
  const apiTrialDayCurrent = Number(billing.trial_day_current);
  let dayIndex = Number.isFinite(apiTrialDayCurrent) && apiTrialDayCurrent > 0
    ? Math.floor(apiTrialDayCurrent)
    : 1;
  if (dayIndex <= 0) {
    const startedStr = billing.trial_started_at;
    const normalized =
      typeof startedStr === 'string' && startedStr
        ? (startedStr.includes('T') ? startedStr : startedStr.replace(' ', 'T'))
        : '';
    const started = normalized ? new Date(normalized) : null;
    const validStart = started && Number.isFinite(started.getTime());
    dayIndex = validStart
      ? Math.max(1, Math.floor((nowMs - started.getTime()) / (24 * 60 * 60 * 1000)) + 1)
      : 1;
  }

  if (dayIndex <= trialDays) {
    const remaining = Math.max(0, trialDays - dayIndex + 1);
    const dayWord = remaining === 1 ? 'day' : 'days';
    return {
      tone: 'info',
      title: 'You are currently on a free trial.',
      body: `You have ${remaining} ${dayWord} remaining to activate your plan.`,
    };
  }

  if (dayIndex <= trialDays * 2) {
    return {
      tone: 'warning',
      title: 'Your trial period has expired.',
      body: 'Please approve the app charge soon to avoid service interruption in your store.',
    };
  }

  return {
    tone: 'critical',
    title: 'The app has been disabled because the charge was not approved.',
    body: 'Approve the charge to restore access.',
  };
}

// GraphQL query for themes
const THEMES_QUERY = {
  query: `query GetStoreThemes {
    themes(first: 10, roles: MAIN) {
      edges {
        node {
          id
          name
          role
          files(filenames: ["config/settings_data.json"]) {
            edges {
              node {
                body {
                  ... on OnlineStoreThemeFileBodyText {
                    content
                  }
                }
              }
            }
          }
        }
      }
    }
  }`
};

// Memoized Theme Extension Card Component
const ThemeExtensionCard = memo(({ 
  appBlockDisabled, 
  themeId, 
  themeLoading, 
  onRefresh, 
  onEnable 
}) => {
  return (
    <Box paddingBlockEnd="400">
      <Card sectioned>
        <BlockStack gap="300">
          <InlineStack gap="400" align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h3" variant="headingMd" fontWeight="semibold">
                Theme Extension
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {appBlockDisabled 
                  ? "Please enable the app block to activate campaigns in your store."
                  : "The app block is already enabled and campaigns are active in your store."
                }
              </Text>
            </BlockStack>
            <InlineStack gap="300" blockAlign="center">
              <InlineStack gap="200" blockAlign="center">
                <Tooltip content="Refresh theme status">
                  <Button
                    icon={RefreshIcon}
                    onClick={onRefresh}
                    variant="tertiary"
                    size="slim"
                    loading={themeLoading}
                    disabled={themeLoading}
                  />
                </Tooltip>
                <Badge tone={appBlockDisabled ? "critical" : "success"}>
                  {appBlockDisabled ? "Not Active" : "Active"}
                </Badge>
              </InlineStack>
              <Tooltip content={appBlockDisabled ? "Open theme editor to enable the campaign widget" : "Theme widget is already enabled"}>
                <Button
                  icon={appBlockDisabled ? ExternalIcon : undefined}
                  onClick={onEnable}
                  variant="primary"
                  disabled={!themeId || themeLoading || !appBlockDisabled}
                >
                  {appBlockDisabled ? "Enable in theme editor" : "Enabled theme"}
                </Button>
              </Tooltip>
            </InlineStack>
          </InlineStack>
        </BlockStack>
      </Card>
    </Box>
  );
});

ThemeExtensionCard.displayName = 'ThemeExtensionCard';

function HomeContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const app = useSafeAppBridge();

  // Get install state from Redux (using reusable hook - ensures install API is only called once)
  const {
    installChecked,
    installSuccess,
    installLoading,
    installData,
    shop,
    host,
  } = useEnsureInstall();

  // State for Shopify store themes and app block status
  const [appBlockDisabled, setAppBlockDisabled] = useState(false);
  const [themeId, setThemeId] = useState(null);
  const [themeLoading, setThemeLoading] = useState(false);
  
  // Refs to prevent duplicate API calls on initial load
  const themesLoadedRef = useRef(false);
  const initialLoadDoneRef = useRef(false); // Track if initial load APIs have been called
  const campaignsLoadedRef = useRef(null); // Track clientId for campaigns to prevent duplicate calls
  
  // Get onboarding status from install API response
  const onboardingStatusFromBackend = useMemo(() => {
    return installData?.shop_info?.is_on_boarding || '0';
  }, [installData]);

  /** Complete Setup saved in API (`is_on_boarding === '3'`). Used to mark steps 1–5 checked and drive billing expand. */
  const completeSetupDone = useMemo(
    () => String(onboardingStatusFromBackend) === '3',
    [onboardingStatusFromBackend],
  );
  
  // State for Setup Guide steps completion
  const [step4Completed, setStep4Completed] = useState(false);
  const [step3Completed, setStep3Completed] = useState(false);
  const [step5Completed, setStep5Completed] = useState(false);
  const [step6Completed, setStep6Completed] = useState(false);
  const [billingApprovalLoading, setBillingApprovalLoading] = useState(false);
  /** Recomputed on interval so trial day count decreases without full page reload. */
  const [trialClockTick, setTrialClockTick] = useState(() => Date.now());

  // Track the highest onboarding status sent to avoid duplicate API calls
  const lastOnboardingStatusSentRef = useRef(null);
  
  // Initialize step completion states from backend onboarding status
  // Reset initialization when installData changes to handle navigation back
  useEffect(() => {
    if (!installChecked || !installData) return;
    
    const backendStatus = onboardingStatusFromBackend;
    
    // Initialize step states based on backend status
    // Status '1': Step 1 completed (has campaigns)
    // Status '2': Step 3 completed (app block active) 
    // Status '3': Step 5 completed (campaign working - Complete Setup)
    // Status '4': Step 2 completed (widget block added)
    // Status '5': Step 4 completed (checkout rules added)
    
    if (backendStatus === '4' || backendStatus === '5') {
      setStep4Completed(true);
    } else {
      setStep4Completed(false);
    }
    
    // Step 4 (checkout rules) is completed if status is 5 or 3 (status 3 means setup is complete, so step 4 was done)
    if (backendStatus === '5' || backendStatus === '3') {
      setStep5Completed(true);
    } else {
      // Reset if status is less than 5
      if (parseInt(backendStatus) < 5) {
        setStep5Completed(false);
      }
    }
    
    if (backendStatus === '3') {
      setStep3Completed(true);
    } else {
      // Only reset if status is less than 3 (don't reset if we're at 3 or higher)
      if (parseInt(backendStatus) < 3) {
        setStep3Completed(false);
      }
    }
    
    // Initialize the ref with backend status to prevent unnecessary API calls
    if (backendStatus !== '0') {
      lastOnboardingStatusSentRef.current = backendStatus;
    }
    
    // Allow re-initialization when installData changes (e.g., when navigating back)
    // This ensures we always have the latest onboarding status from backend
  }, [installChecked, installData, onboardingStatusFromBackend]);

  useEffect(() => {
    if (!installChecked || !installData?.shop_info) return;
    if (String(installData.shop_info.user_type) === '0') {
      setStep6Completed(false);
      return;
    }
    const approved =
      String(installData.shop_info.is_charge_approved) === '1' ||
      installData.shop_info.billing?.billing_status === 'active';
    setStep6Completed(!!approved);
  }, [installChecked, installData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('billing_return') !== '1') return;
    dispatch(checkInstall({ force: true }));
    params.delete('billing_return');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true });
  }, [location.search, location.pathname, dispatch, navigate]);

  useEffect(() => {
    const id = window.setInterval(
      () => setTrialClockTick(Date.now()),
      60 * 60 * 1000,
    );
    return () => window.clearInterval(id);
  }, []);

  const billingApprovalNotice = useMemo(() => {
    const billing = installData?.shop_info?.billing;
    return computeBillingApprovalNotice(billing, trialClockTick);
  }, [installData, trialClockTick]);

  // Filter state - always use filtered API, so hasFiltersApplied is true by default
  const [filters, setFiltersState] = useState({
    selected: 0,
    queryValue: '',
    statusFilter: [],
    typeFilter: [],
    goalRange: DEFAULT_GOAL_RANGE,
    sortSelected: ['id desc'], // Default: Sort by ID descending (newest first)
    hasFiltersApplied: true // Always use filtered API with default filters
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Get campaigns from Redux
  const { 
    campaigns: reduxCampaigns, 
    filteredCampaigns,
    loading: campaignsLoading, 
    filteredLoading: campaignsFilteredLoading,
    hasCampaignsEver: reduxHasCampaignsEver,
    pagination: reduxPagination
  } = useSelector((state) => state.campaign);

  // Always use filtered campaigns (always using filtered API)
  const campaigns = useMemo(() => {
    return filteredCampaigns;
  }, [filteredCampaigns]);

  // Use pagination from Redux based on whether filters are applied
  const pagination = useMemo(() => {
    return filters.hasFiltersApplied ? reduxPagination : reduxPagination;
  }, [filters.hasFiltersApplied, reduxPagination]);

  const hasCampaignsEver = reduxHasCampaignsEver;
  const loading = campaignsFilteredLoading; // Always use filtered loading state
  const tableLoading = campaignsFilteredLoading;

  // Redirect on install failure (no HTML shown)
  useEffect(() => {
    if (installChecked && !installSuccess && !installData && shop) {
      const redirectUrl = buildNodeApiEntryUrl(shop, host);

      // Try App Bridge redirect first
      if (app) {
        try {
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, redirectUrl);
          return;
        } catch (error) {
          console.warn('[Home] App Bridge redirect failed, trying fallback');
        }
      }

      // Fallback: window navigation
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = redirectUrl;
        } else {
          window.location.href = redirectUrl;
        }
      } catch (error) {
        window.open(redirectUrl, '_blank');
      }
    }
  }, [installChecked, installSuccess, installData, shop, host, app]);

  // Sync URL with App Bridge History when route changes
  useEffect(() => {
    if (!app || !installChecked || !installData) return;
    
    try {
      const history = History.create(app);
      // Update parent window URL to match current route
      history.dispatch(History.Action.REPLACE, location.pathname);
    } catch (error) {
      console.warn('[Home] App Bridge History sync failed:', error);
    }
  }, [location.pathname, app, installChecked, installData]);

  // Fetch Shopify store themes and check app block status
  // Extracted as a reusable function that can be called manually
  const fetchStoreThemes = useCallback(async (forceRefresh = false) => {
    if (!installChecked || !installData) return;
    if (!window.shopify) return;
    
    // Prevent duplicate calls unless forced refresh
    if (!forceRefresh && themesLoadedRef.current) return;
    themesLoadedRef.current = true;

    setThemeLoading(true);
    try {
      const response = await graphqlCall(THEMES_QUERY);
      
      if (response.errors) {
        throw new Error(response.errors[0]?.message || 'Failed to fetch store themes');
      }

      const themes = response.data?.themes?.edges;
      if (!themes?.length) {
        console.warn('[Home] No themes found');
        setThemeLoading(false);
        return;
      }

      // Extract theme ID from GraphQL ID format: gid://shopify/OnlineStoreTheme/181638103346
      const themeNode = themes[0].node;
      let extractedThemeId = null;
      
      if (themeNode?.id) {
        const themeIdMatch = themeNode.id.match(/\/(\d+)$/);
        if (themeIdMatch && themeIdMatch[1]) {
          extractedThemeId = themeIdMatch[1];
          setThemeId(extractedThemeId);
        }
      }

      // Check app block status if files are available
      const files = themeNode.files?.edges;
      if (files?.length) {
        try {
          // Strip comments and parse JSON
          const contentString = files[0].node.body.content.replace(/\/\*[\s\S]*?\*\//g, '').trim();
          const settingsData = JSON.parse(contentString);
          const blocks = settingsData?.current?.blocks || {};
          
          // Find app blocks that match our app block type
          const appBlocks = Object.values(blocks).filter(block => {
            // Check if block type includes/starts with our app block type
            return block?.type && (
              block.type.startsWith(APP_BLOCK_TYPE) || 
              block.type.includes('shopify://apps/lift-presales/blocks/campaign') || 
              block.type.includes('shopify://apps/lift-crowdfunding-presales/blocks/campaign')
            );
          });
          
          // Set appBlockDisabled based on whether any enabled (not disabled) block exists
          // If at least one block exists with disabled: false, the block is active
          const hasEnabledBlock = appBlocks.length > 0 && appBlocks.some(block => {
            // Block is enabled if disabled is false or undefined (default is enabled)
            return block.disabled === false || block.disabled === undefined;
          });
          
          // appBlockDisabled = false means block is active
          // appBlockDisabled = true means block is not active
          setAppBlockDisabled(!hasEnabledBlock);
        } catch (parseError) {
          console.error('[Home] Error parsing theme settings:', parseError);
          setAppBlockDisabled(true);
        }
      } else {
        // If no files, assume block is disabled
        setAppBlockDisabled(true);
      }
    } catch (error) {
      console.error('[Home] Error fetching store themes:', error);
      setAppBlockDisabled(true);
      // Reset ref on error so it can be retried
      if (!forceRefresh) {
        themesLoadedRef.current = false;
      }
    } finally {
      setThemeLoading(false);
    }
  }, [installChecked, installData]);

  // Local UI state
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  // Handle refresh button click - optimized
  const handleRefreshTheme = useCallback(async () => {
    themesLoadedRef.current = false; // Reset ref to allow refresh
    await fetchStoreThemes(true); // Force refresh
    setToastMessage('Theme status refreshed');
    setShowToast(true);
  }, [fetchStoreThemes]);

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationChecked, setDeleteConfirmationChecked] = useState(false);
  
  // State to track which step is expanded (only one at a time)
  const [expandedStep, setExpandedStep] = useState(null);

  // Extract filter values
  const { selected, queryValue, statusFilter, typeFilter, goalRange, sortSelected, hasFiltersApplied } = filters;

  // Calculate completed steps dynamically - each step completes independently
  // Step 1: Complete when campaigns exist (check campaign length dynamically)
  // Step 2: Complete when app block is active (appBlockDisabled === false means block is active) - always check theme status
  // Step 3: Complete when preview is clicked and onboarding status is updated to 3
  const hasCampaigns = useMemo(() => {
    // Check campaign length dynamically - campaigns exist if length > 0
    return campaigns.length > 0 || hasCampaignsEver;
  }, [campaigns.length, hasCampaignsEver]);

  const isLegacyCustomer = useMemo(
    () => String(installData?.shop_info?.user_type) === '0',
    [installData],
  );
  const showBillingSetupStep = useMemo(() => !isLegacyCustomer, [isLegacyCustomer]);
  const totalSetupSteps = useMemo(
    () => (showBillingSetupStep ? TOTAL_STEPS : TOTAL_STEPS - 1),
    [showBillingSetupStep],
  );

  const completedSteps = useMemo(() => {
    if (completeSetupDone) {
      if (showBillingSetupStep) {
        return step6Completed ? TOTAL_STEPS : TOTAL_STEPS - 1;
      }
      return TOTAL_STEPS - 1;
    }
    let steps = 0;
    if (hasCampaigns) steps++; // Step 1 - complete when campaigns exist
    if (step4Completed) steps++; // Step 2 - complete when widget block is added
    if (!appBlockDisabled) steps++; // Step 3 - complete only when appBlockDisabled is false (block is active) - always check theme
    if (step5Completed) steps++; // Step 4 - complete when checkout rules are added
    if (step3Completed) steps++; // Step 5 - complete when preview is clicked and onboarding status updated
    if (showBillingSetupStep && step6Completed) steps++; // Step 6 - new customers only
    return steps;
  }, [
    completeSetupDone,
    hasCampaigns,
    appBlockDisabled,
    step3Completed,
    step4Completed,
    step5Completed,
    step6Completed,
    showBillingSetupStep,
  ]);
  
  // Track which steps are completed individually (steps 1–5 implied done once API reports Complete Setup)
  const stepCompletionStatus = useMemo(() => ({
    step1: hasCampaigns || completeSetupDone,
    step2: step4Completed || completeSetupDone,
    step3: !appBlockDisabled || completeSetupDone,
    step4: step5Completed || completeSetupDone,
    step5: step3Completed || completeSetupDone,
    step6: step6Completed,
  }), [hasCampaigns, appBlockDisabled, step3Completed, step4Completed, step5Completed, step6Completed, completeSetupDone]);
  
  // Initialize expanded step - find first incomplete step based on onboarding status
  const expandedStepInitializedRef = useRef(false);
  useEffect(() => {
    // Find the first incomplete step based on current step completion status
    // This ensures we open the next step that needs to be completed
    let firstIncompleteStep = null;
    
    // Check steps in order and find the first incomplete one
    for (let i = 1; i <= totalSetupSteps; i++) {
      const stepKey = `step${i}`;
      if (!stepCompletionStatus[stepKey]) {
        firstIncompleteStep = stepKey;
        break;
      }
    }
    
    // If all steps are completed, open the last step
    if (!firstIncompleteStep) {
      firstIncompleteStep = `step${totalSetupSteps}`;
    }
    
    // Initialize once with the first incomplete step
    if (!expandedStepInitializedRef.current) {
      if (firstIncompleteStep) {
        setExpandedStep(firstIncompleteStep);
      }
      expandedStepInitializedRef.current = true;
    } else {
      // Update when step completion status changes (which depends on onboarding status)
      // Only auto-update if no step is currently expanded or if current step becomes completed
      if (firstIncompleteStep) {
        // If current expanded step is completed, switch to first incomplete step
        if (expandedStep && stepCompletionStatus[expandedStep] && expandedStep !== firstIncompleteStep) {
          setExpandedStep(firstIncompleteStep);
        } else if (!expandedStep) {
          // If no step is expanded, expand the first incomplete one
          setExpandedStep(firstIncompleteStep);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepCompletionStatus, onboardingStatusFromBackend, totalSetupSteps, completeSetupDone]); // Depend on step completion status and onboarding status

  // After Complete Setup (status 3), open billing / Active Plan step only when charge still pending
  useEffect(() => {
    if (!completeSetupDone || !showBillingSetupStep || step6Completed) return;
    setExpandedStep('step6');
  }, [completeSetupDone, showBillingSetupStep, step6Completed]);
  
  // Open a specific step and close all others
  const openStep = useCallback((stepKey) => {
    setExpandedStep(prev => prev === stepKey ? null : stepKey); // Toggle: if already open, close it; otherwise open it
  }, []);
  
  // Update onboarding status when steps complete - optimized to send highest completed step
  // IMPORTANT: Don't override status 3 (complete setup) with lower statuses
  // OPTIMIZATION: Skip during initial load to prevent extra API calls
  useEffect(() => {
    if (!installChecked || !installData) return;
    
    // Skip during initial load to prevent extra API calls
    if (!initialLoadDoneRef.current) return;
    
    const clientId = installData?.shop_info?.id;
    if (!clientId) return;
    
    // Get current backend status from Redux (source of truth)
    const backendStatus = parseInt(onboardingStatusFromBackend);
    
    // If status is already 3 (complete setup), don't auto-update to lower statuses
    // This prevents overriding "Complete Setup" with status 4 or 2
    if (backendStatus >= 3) {
      return; // Don't auto-update if setup is already complete
    }
    
    // Determine the highest completed step status
    let highestStatus = '0'; // Default: No step
    
    // Priority: status 3 > status 5 > status 4 > status 2 > status 1
    if (step3Completed) {
      highestStatus = '3'; // Step 5: Campaign working (Complete Setup)
    } else if (step5Completed) {
      highestStatus = '5'; // Step 4: Checkout rules added
    } else if (step4Completed) {
      highestStatus = '4'; // Step 2: Widget block added
    } else if (!appBlockDisabled) {
      highestStatus = '2'; // Step 3: Active app block (always check theme status)
    } else if (hasCampaigns) {
      highestStatus = '1'; // Step 1: Create campaign
    }
    
    const currentStatus = parseInt(highestStatus);
    const lastSentStatus = lastOnboardingStatusSentRef.current !== null 
      ? parseInt(lastOnboardingStatusSentRef.current) 
      : backendStatus; // Use backend status as initial reference
    
    // Only update if current status is higher than what we've sent or what backend has
    // And only if we haven't reached status 3 yet
    if (currentStatus > lastSentStatus && backendStatus < 3) {
      const updateOnboarding = async () => {
        try {
          const response = await clientAPI.updateOnboardingStatus(clientId, highestStatus);
          
          if (response.success) {
            // Update Redux store with new onboarding status to keep it in sync
            dispatch(updateOnboardingStatus({ onboardingStatus: highestStatus }));
            lastOnboardingStatusSentRef.current = highestStatus;
          }
        } catch (error) {
          console.error('[Home] Failed to update onboarding status:', error);
          // Don't show toast for onboarding errors to avoid annoying users
        }
      };
      
      updateOnboarding();
    }
  }, [hasCampaigns, appBlockDisabled, step3Completed, step4Completed, step5Completed, installChecked, installData, onboardingStatusFromBackend, dispatch]);
  
  const { mode, setMode } = useSetIndexFiltersMode();
  const breakpoints = useBreakpoints();

  // Refs for filter management
  const isLoadingRef = useRef(false);
  const lastFiltersRef = useRef(null); // Track last filters to avoid unnecessary fetches
  const debounceTimeoutRef = useRef(null); // Track debounce timeout for proper cleanup

  // Load campaigns using filtered API with default filters - memoized with useCallback (with pagination)
  const loadCampaigns = useCallback(async () => {
    try {
      // Use filtered campaigns API with default filters (empty filters = all campaigns)
      const backendFilters = {};
      
      // Parse default sorting
      let sortingField = 'id'; // Default to ID
      let sortingDirection = 'desc'; // Default to descending (newest first)
      if (filters.sortSelected && filters.sortSelected.length > 0) {
        const [field, direction] = filters.sortSelected[0].split(' ');
        sortingField = field;
        sortingDirection = direction || 'desc';
      }

      const filterKey = JSON.stringify({
        page: currentPage,
        search: '',
        sorting: { field: sortingField, direction: sortingDirection },
        filters: backendFilters,
      });

      // Check if we already have this data
      if (lastFiltersRef.current === filterKey && filteredCampaigns.length > 0) {
        return;
      }

      // Dispatch filtered campaigns API with default filters and pagination
      const result = await dispatch(fetchFilteredCampaigns({
        client_id: installData?.shop_info?.id || 1,
        page: currentPage,
        limit: 10, // Set limit to 10 items per page
        search: '', // No search by default
        sorting: {
          field: sortingField,
          direction: sortingDirection,
        },
        filters: backendFilters,
      }));

      if (fetchFilteredCampaigns.rejected.match(result)) {
        throw new Error(result.payload || 'Failed to load campaigns');
      } else {
        lastFiltersRef.current = filterKey;
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setToastMessage(error.message || 'Network error: Unable to connect to the server');
      setShowToast(true);
    }
  }, [dispatch, installData, currentPage, filters.sortSelected, filteredCampaigns.length]);

  // Mapping functions - memoized
  const mapStatusToBackend = useCallback((status) => {
    const statusMap = { 'active': '1', 'draft': '0', 'expired': '2', 'scheduled': '2' };
    return statusMap[status.toLowerCase()] || null;
  }, []);

  const mapTypeToBackend = useCallback((type) => {
    const typeMap = { 'presales': 'PreSales', 'crowdfunding': 'Crowdfunding', 'product drop': 'Product Drop' };
    return typeMap[type.toLowerCase()] || null;
  }, []);

  // Load filtered campaigns using Redux - memoized with useCallback
  const loadFilteredCampaigns = useCallback(async (pageOverride = null) => {
    try {
      // Use pageOverride if provided, otherwise use currentPage
      const pageToUse = pageOverride !== null ? pageOverride : currentPage;
      
      // Build filters object for backend
      const backendFilters = {};

      // Status filter mapping
      const selectedTab = TAB_STRINGS[filters.selected];
      if (selectedTab !== 'All') {
        const statusMap = { 'Active': '1', 'Draft': '0', 'Expired': '2', 'Scheduled': '2' };
        if (statusMap[selectedTab]) backendFilters.status = statusMap[selectedTab];
      } else if (filters.statusFilter.length > 0) {
        const mappedStatus = mapStatusToBackend(filters.statusFilter[0]);
        if (mappedStatus) backendFilters.status = mappedStatus;
      }

      // Type filter - map to backend format
      if (filters.typeFilter.length > 0) {
        const mappedTypes = filters.typeFilter
          .map(mapTypeToBackend)
          .filter(Boolean);
        if (mappedTypes.length > 0) {
          backendFilters.type = mappedTypes;
        }
      }

      // Goal filter - use minimum value from range
      if (filters.goalRange[0] > 0) {
        backendFilters.Goal = filters.goalRange[0];
      }

      // Parse sorting
      let sortingField = 'id'; // Default to ID
      let sortingDirection = 'desc'; // Default to descending (newest first)
      if (filters.sortSelected && filters.sortSelected.length > 0) {
        const [field, direction] = filters.sortSelected[0].split(' ');
        sortingField = field;
        sortingDirection = direction || 'desc';
      }

      // Create a unique key for these filters
      const filterKey = JSON.stringify({
        page: pageToUse,
        search: filters.queryValue || '',
        sorting: { field: sortingField, direction: sortingDirection },
        filters: backendFilters,
      });

      // Check if we already have this exact data in Redux
      if (lastFiltersRef.current === filterKey && filteredCampaigns.length > 0 && !campaignsFilteredLoading) {
        return;
      }

      // Dispatch Redux action with pagination
      const result = await dispatch(fetchFilteredCampaigns({
        client_id: installData?.shop_info?.id || 1,
        page: pageToUse,
        limit: 10, // Set limit to 10 items per page
        search: filters.queryValue || '',
        sorting: {
          field: sortingField,
          direction: sortingDirection,
        },
        filters: backendFilters,
      }));

      if (fetchFilteredCampaigns.rejected.match(result)) {
        throw new Error(result.payload || 'Failed to load filtered campaigns');
      } else {
        // Store the filter key after successful fetch
        lastFiltersRef.current = filterKey;
      }
    } catch (error) {
      console.error('Error loading filtered campaigns:', error);
      setToastMessage(error.message || 'Network error: Unable to connect to the server');
      setShowToast(true);
    }
  }, [dispatch, filters, mapStatusToBackend, mapTypeToBackend, installData, currentPage, filteredCampaigns.length, campaignsFilteredLoading]);

  // OPTIMIZATION: Initial load - run Install (via useEnsureInstall), filtered campaigns API, and theme GraphQL in parallel
  // Only runs once on page refresh
  useEffect(() => {
    // Only load if install check is complete and successful
    if (!installChecked || !installData) {
      return;
    }
    
    // Prevent duplicate calls - check if already loaded for this clientId
    const clientId = installData?.shop_info?.id || 1;
    if (initialLoadDoneRef.current || campaignsLoadedRef.current === clientId) {
      return;
    }
    
    // Mark as loading immediately to prevent duplicate calls
    initialLoadDoneRef.current = true;
    campaignsLoadedRef.current = clientId;
    
    // Check if campaigns already exist in Redux (use cached data if available)
    const hasExistingCampaigns = filteredCampaigns.length > 0;
    
    // Run filtered campaigns API and theme GraphQL in parallel for faster load
    const loadInitialData = async () => {
      const promises = [];
      
      // Only fetch campaigns if not in Redux
      if (!hasExistingCampaigns && !campaignsFilteredLoading && !isLoadingRef.current) {
        isLoadingRef.current = true;
        promises.push(
          loadCampaigns().finally(() => {
            isLoadingRef.current = false;
          })
        );
      }
      
      // Fetch themes GraphQL (only if not already loaded)
      if (!themesLoadedRef.current && window.shopify) {
        promises.push(fetchStoreThemes());
      }
      
      // Wait for all initial APIs to complete
      await Promise.allSettled(promises);
    };
    
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installChecked, installData?.shop_info?.id]); // Only depend on install check and clientId

  // Load filtered campaigns when filters/search/sort/tabs change or page changes
  // Optimized to ensure only one API call per filter change
  useEffect(() => {
    // Skip if install check is not ready
    if (!installChecked || !installData) return;
    
    // Skip if initial load hasn't completed yet (prevent filter changes during initial load)
    if (!initialLoadDoneRef.current) return;
    
    // Create filter key to check if filters actually changed
    const selectedTab = TAB_STRINGS[filters.selected];
    const backendFilters = {};
    if (selectedTab !== 'All') {
      const statusMap = { 'Active': '1', 'Draft': '0', 'Expired': '2', 'Scheduled': '2' };
      if (statusMap[selectedTab]) backendFilters.status = statusMap[selectedTab];
    }
    
    let sortingField = 'id'; // Default to ID
    let sortingDirection = 'desc'; // Default to descending (newest first)
    if (filters.sortSelected && filters.sortSelected.length > 0) {
      const [field, direction] = filters.sortSelected[0].split(' ');
      sortingField = field;
      sortingDirection = direction || 'desc';
    }
    
    const currentFilterKey = JSON.stringify({
      page: currentPage,
      search: filters.queryValue || '',
      status: filters.statusFilter,
      type: filters.typeFilter,
      goalRange: filters.goalRange,
      selected: filters.selected,
      sorting: { field: sortingField, direction: sortingDirection },
      filters: backendFilters,
    });
    
    // If filters haven't changed, don't fetch (prevent infinite loops)
    // Only check filter key, not data length, to avoid re-triggering when data changes
    if (lastFiltersRef.current === currentFilterKey) {
      return;
    }
    
    // If already loading, skip to prevent duplicate calls
    if (isLoadingRef.current || campaignsFilteredLoading) {
      return;
    }
    
    // Cancel any pending debounce timeout to ensure only one call happens
    // This is key: when filters change, we cancel the previous debounce and start a new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    
    // Debounce API calls - ensures only the latest filter change triggers an API call after 300ms
    // If filters change multiple times within 300ms, only the last change will trigger a call
    debounceTimeoutRef.current = setTimeout(async () => {
      try {
        // Set loading flag to prevent duplicate calls
        isLoadingRef.current = true;
        await loadFilteredCampaigns();
      } catch (error) {
        console.error('[Home] Error in debounced filter load:', error);
      } finally {
        isLoadingRef.current = false;
        debounceTimeoutRef.current = null;
      }
    }, 300); // 300ms debounce - ensures only one API call per filter change

    return () => {
      // Clean up timeout on unmount or when dependencies change
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, [filters, currentPage, loadFilteredCampaigns, installChecked, installData, campaignsFilteredLoading]);

  // Memoized helper functions
  const getCampaignStatus = useCallback((campaign_status) => {
    // Use enum numbers directly from API response
    switch (campaign_status) {
      case '0': return 'Draft';
      case '1': return 'Active';
      case '2': return 'Expired';
      default: return 'Draft';
    }
  }, []);

  // Get status for tab filtering (including Scheduled logic) - memoized
  const getTabStatus = useCallback((campaign) => {
    const now = new Date();
    const startDate = new Date(campaign.campaign_start_datetime);

    // If campaign hasn't started yet, it's Scheduled
    if (now < startDate) return 'Scheduled';

    // Otherwise use the campaign_status
    return getCampaignStatus(campaign.campaign_status);
  }, [getCampaignStatus]);

  const getCampaignType = useCallback((campaign_type) => {
    switch (campaign_type) {
      case '0': return 'PreSales';
      case '1': return 'Crowdfunding';
      case '2': return 'Product Drop';
      default: return 'Unknown';
    }
  }, []);

  // Sort options - memoized
  const sortOptions = useMemo(() => [
    { label: 'ID', value: 'id desc', directionLabel: 'Newest First' },
    { label: 'ID', value: 'id asc', directionLabel: 'Oldest First' },
    { label: 'Campaign Name', value: 'campaign_name asc', directionLabel: 'A-Z' },
    { label: 'Campaign Name', value: 'campaign_name desc', directionLabel: 'Z-A' },
    { label: 'Status', value: 'campaign_status asc', directionLabel: 'A-Z' },
    { label: 'Status', value: 'campaign_status desc', directionLabel: 'Z-A' },
    { label: 'Type', value: 'campaign_type asc', directionLabel: 'A-Z' },
    { label: 'Type', value: 'campaign_type desc', directionLabel: 'Z-A' },
    { label: 'End Date', value: 'campaign_end_datetime asc', directionLabel: 'Oldest First' },
    { label: 'End Date', value: 'campaign_end_datetime desc', directionLabel: 'Newest First' },
    { label: 'Initial Fund', value: 'initial_fund asc', directionLabel: 'Low to High' },
    { label: 'Initial Fund', value: 'initial_fund desc', directionLabel: 'High to Low' },
    { label: 'Goal', value: 'total_amount asc', directionLabel: 'Low to High' },
    { label: 'Goal', value: 'total_amount desc', directionLabel: 'High to Low' },
  ], []);

  // Table headings - memoized to prevent re-renders
  const tableHeadings = useMemo(() => [
    { title: 'Campaign name' },
    { title: 'Status' },
    { title: 'Type' },
    { title: 'End date' },
    { title: 'Achieved', alignment: 'end' },
    { title: 'Items/Amount Sold', alignment: 'end' },
    { title: 'Initial funds', alignment: 'end' },
    { title: 'Goal', alignment: 'end' },
    { title: 'Progress' },
  ], []);

  

  // Campaigns are already filtered and sorted from backend when hasFiltersApplied is true
  // Apply frontend filtering for goal range only (Scheduled is now handled by backend) - optimized
  const displayedCampaigns = useMemo(() => {
    if (!campaigns.length) return [];
    
    let filtered = campaigns;
    
    // Apply goal range filter on frontend (backend only supports >=, we need range)
    if (goalRange[0] > 0 || goalRange[1] < DEFAULT_GOAL_RANGE[1]) {
      filtered = filtered.filter(campaign => {
        const goalValue = campaign.goal_type === '0' ? campaign.number_of_items : campaign.total_amount;
        return goalValue >= goalRange[0] && goalValue <= goalRange[1];
      });
    }
    
    return filtered;
  }, [campaigns, goalRange]);

  // Get money format from install data
  const moneyFormat = useMemo(() => {
    return installData?.shop_info?.money_format || '${{amount}}';
  }, [installData]);

  // Extract currency prefix from money_format (e.g., "$" from "${{amount}}")
  const currencyPrefix = useMemo(() => {
    if (!moneyFormat) return '$';
    // Extract everything before {{amount}}
    const match = moneyFormat.match(/^([^{]*)/);
    return match ? match[1] : '$';
  }, [moneyFormat]);

  // Format currency - memoized - uses money format from install API
  const formatCurrency = useCallback((amount, decimals = 0) => {
    return formatMoney(amount, moneyFormat, { decimals });
  }, [moneyFormat]);

  // Get status badge tone - memoized
  const getStatusTone = useCallback((status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Draft': return 'info';
      case 'Expired': return 'success';
      case 'Scheduled': return 'info';
      default: return 'subdued';
    }
  }, []);

  // Get type badge tone - memoized
  const getTypeTone = useCallback((type) => {
    switch (type) {
      case 'Crowdfunding': return 'success';
      case 'Product Drop': return 'info';
      case 'PreSales': return 'warning';
      default: return 'subdued';
    }
  }, []);

  const setFilters = useCallback((updates) => {
    setFiltersState(prev => ({ ...prev, ...updates }));
  }, []);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => 
    queryValue.length > 0 || statusFilter.length > 0 || typeFilter.length > 0 ||
    goalRange[0] > 0 || goalRange[1] < DEFAULT_GOAL_RANGE[1] || selected > 0 ||
    (sortSelected?.[0] !== 'id desc'), // Check if sort is not default (ID desc)
    [queryValue, statusFilter, typeFilter, goalRange, selected, sortSelected]
  );

  // Filter handlers - trigger filtered API call (loading managed by Redux)
  const handleStatusFilterChange = useCallback((value) => {
    setCurrentPage(1); // Reset to page 1 when filter changes
    setFilters({ statusFilter: value, hasFiltersApplied: true });
  }, [setFilters]);

  const handleTypeFilterChange = useCallback((value) => {
    setCurrentPage(1); // Reset to page 1 when filter changes
    setFilters({ typeFilter: value, hasFiltersApplied: true });
  }, [setFilters]);

  const handleGoalRangeChange = useCallback((value) => {
    setCurrentPage(1); // Reset to page 1 when filter changes
    setFilters({ goalRange: value, hasFiltersApplied: true });
  }, [setFilters]);

  const handleQueryChange = useCallback((value) => {
    setCurrentPage(1); // Reset to page 1 when search changes
    // Always trigger filtered API when search changes (even if empty, other filters might be active)
    setFilters({ queryValue: value, hasFiltersApplied: true });
  }, [setFilters]);

  // Helper to check if other filters are active
  const checkOtherFiltersActive = useCallback((excludeKey) => {
    const checks = {
      queryValue: queryValue.length > 0,
      statusFilter: statusFilter.length > 0,
      typeFilter: typeFilter.length > 0,
      goalRange: goalRange[0] > 0 || goalRange[1] < DEFAULT_GOAL_RANGE[1],
      selected: selected > 0,
      sortSelected: sortSelected?.[0] !== 'id desc' // Check if sort is not default (ID desc)
    };
    delete checks[excludeKey];
    return Object.values(checks).some(Boolean);
  }, [queryValue, statusFilter, typeFilter, goalRange, selected, sortSelected]);

  // Filter removal handlers - always use filtered API
  const handleStatusFilterRemove = useCallback(() => {
    setCurrentPage(1); // Reset to page 1 when removing filter
    setFilters({ statusFilter: [], hasFiltersApplied: true }); // Always use filtered API
  }, [setFilters]);

  const handleTypeFilterRemove = useCallback(() => {
    setCurrentPage(1); // Reset to page 1 when removing filter
    setFilters({ typeFilter: [], hasFiltersApplied: true }); // Always use filtered API
  }, [setFilters]);

  const handleGoalRangeRemove = useCallback(() => {
    setCurrentPage(1); // Reset to page 1 when removing filter
    setFilters({ goalRange: DEFAULT_GOAL_RANGE, hasFiltersApplied: true }); // Always use filtered API
  }, [setFilters]);

  const handleQueryRemove = useCallback(() => {
    setCurrentPage(1); // Reset to page 1 when removing query
    setFilters({ queryValue: '', hasFiltersApplied: true }); // Always use filtered API
  }, [setFilters]);

  const handleFiltersClearAll = useCallback(async () => {
    // Reset to page 1 when clearing filters
    setCurrentPage(1);
    
    // Always use filtered API, even when clearing filters (will use default/empty filters)
    setFilters({
      statusFilter: [],
      typeFilter: [],
      goalRange: DEFAULT_GOAL_RANGE,
      queryValue: '',
      selected: 0,
      sortSelected: ['id desc'], // Default: Sort by ID descending (newest first)
      hasFiltersApplied: true // Always use filtered API
    });
    
    // Small delay to ensure state updates before loading
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Use filtered campaigns API with default filters
    await loadFilteredCampaigns();
  }, [setFilters, loadFilteredCampaigns]);

  const handleTabChange = useCallback((index) => {
    const selectedTab = TAB_STRINGS[index];

    // Reset to page 1 when tab changes
    setCurrentPage(1);

    // Always use filtered API
    if (selectedTab === 'All' && index === 0) {
      setFilters({ selected: index, statusFilter: [], hasFiltersApplied: true });
    } else {
      setFilters({ selected: index, statusFilter: [selectedTab.toLowerCase()], hasFiltersApplied: true });
    }
  }, [setFilters]);

  // Handle sort change (loading managed by Redux)
  const handleSortChange = useCallback((sortOption) => {
    // Reset to page 1 when sort changes
    setCurrentPage(1);
    setFilters({ sortSelected: sortOption, hasFiltersApplied: true });
  }, [setFilters]);

  // Handle pagination change
  const handlePaginationChange = useCallback((page) => {
    setCurrentPage(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Filter definitions - memoized
  const filterDefinitions = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      filter: (
        <ChoiceList
          title="Status"
          titleHidden
          choices={[
            { label: 'Active', value: 'active' },
            { label: 'Draft', value: 'draft' },
            { label: 'Expired', value: 'expired' },
            { label: 'Scheduled', value: 'scheduled' },
          ]}
          selected={statusFilter}
          onChange={handleStatusFilterChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'type',
      label: 'Type',
      filter: (
        <ChoiceList
          title="Type"
          titleHidden
          choices={[
            { label: 'PreSales', value: 'presales' },
            { label: 'Crowdfunding', value: 'crowdfunding' },
            { label: 'Product Drop', value: 'product drop' },
          ]}
          selected={typeFilter}
          onChange={handleTypeFilterChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'goal',
      label: 'Goal Range',
      filter: (
        <RangeSlider
          label="Goal amount is between"
          labelHidden
          value={goalRange}
          prefix="₹"
          output
          min={0}
          max={100000}
          step={1000}
          onChange={handleGoalRangeChange}
        />
      ),
    },
  ], [statusFilter, typeFilter, goalRange, handleStatusFilterChange, handleTypeFilterChange, handleGoalRangeChange]);

  // Applied filters - memoized with proper icons
  const appliedFilters = useMemo(() => {
    const filters = [];
    if (statusFilter.length > 0) {
      filters.push({
        key: 'status',
        label: `Status: ${statusFilter.join(', ')}`,
        onRemove: handleStatusFilterRemove,
      });
    }
    if (typeFilter.length > 0) {
      filters.push({
        key: 'type',
        label: `Type: ${typeFilter.join(', ')}`,
        onRemove: handleTypeFilterRemove,
      });
    }
    if (goalRange[0] > 0 || goalRange[1] < 100000) {
      filters.push({
        key: 'goal',
        label: `Goal: ₹${goalRange[0].toLocaleString()} - ₹${goalRange[1].toLocaleString()}`,
        onRemove: handleGoalRangeRemove,
      });
    }
    return filters;
  }, [statusFilter, typeFilter, goalRange, handleStatusFilterRemove, handleTypeFilterRemove, handleGoalRangeRemove]);


  // Resource name
  const resourceName = {
    singular: 'campaign',
    plural: 'campaigns',
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(displayedCampaigns);

  // Helper function to get product GID from product handle
  const getProductGidFromHandle = useCallback(async (productHandle) => {
    if (!productHandle) return null;
    
    try {
      const query = {
        query: `
          query GetProductByHandle($handle: String!) {
            productByHandle(handle: $handle) {
              id
            }
          }
        `,
        variables: {
          handle: productHandle
        }
      };
      
      const response = await graphqlCall(query);
      return response.data?.productByHandle?.id || null;
    } catch (error) {
      console.warn('[Home] Could not get product GID from handle:', error);
      return null;
    }
  }, []);

  // Helper function to delete product metafields for a campaign
  const deleteCampaignProductMetafields = useCallback(async (campaign) => {
    if (!campaign) return;
    
    try {
      let productGid = null;
      
      // Try to get product GID - check if product_id is available first (more efficient)
      if (campaign.product_id) {
        // Handle product_id - could be numeric or GID format
        if (typeof campaign.product_id === 'string' && campaign.product_id.startsWith('gid://shopify/Product/')) {
          productGid = campaign.product_id;
        } else if (typeof campaign.product_id === 'number' || /^\d+$/.test(String(campaign.product_id))) {
          productGid = `gid://shopify/Product/${campaign.product_id}`;
        } else if (typeof campaign.product_id === 'object' && campaign.product_id?.id) {
          // Handle JSON object format: {"id": 123, ...}
          const productId = campaign.product_id.id;
          if (typeof productId === 'number' || /^\d+$/.test(String(productId))) {
            productGid = `gid://shopify/Product/${productId}`;
          }
        }
      }
      
      // Fallback: Try to get product GID from product_handle if product_id not available
      if (!productGid && campaign.product_handle) {
        productGid = await getProductGidFromHandle(campaign.product_handle);
      }
      
      // If no product GID found, skip metafield deletion
      if (!productGid) {
        console.warn('[Home] Could not get product GID for campaign, skipping metafield deletion:', campaign.id);
        return;
      }
      
      // Delete campaign_data metafield
      try {
        const existingMetafield = await getProductMetafield(productGid, true);
        if (existingMetafield) {
          const response = await deleteProductMetafield(existingMetafield, productGid, 'custom', 'campaign_data');
        }
      } catch (error) {
        console.warn('[Home] Error deleting campaign_data metafield:', error);
      }
      
      // Delete campaign_count metafield
      try {
        await deleteCampaignCountMetafield(productGid);
      } catch (error) {
        console.warn('[Home] Error deleting campaign_count metafield:', error);
      }
    } catch (error) {
      console.error('[Home] Error deleting product metafields for campaign:', error);
    }
  }, [getProductGidFromHandle]);

  // Helper function to update product metafield status for a campaign
  const updateCampaignProductMetafieldStatus = useCallback(async (campaign, newStatus) => {
    if (!campaign || !newStatus) return;
    
    try {
      let productGid = null;
      
      // Try to get product GID - check if product_id is available first (more efficient)
      if (campaign.product_id) {
        // Handle product_id - could be numeric or GID format
        if (typeof campaign.product_id === 'string' && campaign.product_id.startsWith('gid://shopify/Product/')) {
          productGid = campaign.product_id;
        } else if (typeof campaign.product_id === 'number' || /^\d+$/.test(String(campaign.product_id))) {
          productGid = `gid://shopify/Product/${campaign.product_id}`;
        } else if (typeof campaign.product_id === 'object' && campaign.product_id?.id) {
          // Handle JSON object format: {"id": 123, ...}
          const productId = campaign.product_id.id;
          if (typeof productId === 'number' || /^\d+$/.test(String(productId))) {
            productGid = `gid://shopify/Product/${productId}`;
          }
        }
      }
      
      // Fallback: Try to get product GID from product_handle if product_id not available
      if (!productGid && campaign.product_handle) {
        productGid = await getProductGidFromHandle(campaign.product_handle);
      }
      
      // Also check if campaign has products array (for campaigns with multiple products)
      if (!productGid && campaign.products && Array.isArray(campaign.products) && campaign.products.length > 0) {
        // Find primary product or first active product
        const primaryProduct = campaign.products.find(p => p.is_primary_product === '1' && p.is_active === '1') 
          || campaign.products.find(p => p.is_active === '1')
          || campaign.products[0];
        
        if (primaryProduct) {
          // Try to get product GID from shopify_product_id
          if (primaryProduct.shopify_product_id) {
            const shopifyProductId = primaryProduct.shopify_product_id;
            // Handle GID format: "gid://shopify/Product/123456"
            if (typeof shopifyProductId === 'string' && shopifyProductId.startsWith('gid://shopify/Product/')) {
              productGid = shopifyProductId;
            } 
            // Handle numeric ID or string numeric: 123456 or "123456"
            else if (typeof shopifyProductId === 'number' || /^\d+$/.test(String(shopifyProductId))) {
              productGid = `gid://shopify/Product/${shopifyProductId}`;
            }
            // Handle GID format that might need extraction: extract numeric part if needed
            else if (typeof shopifyProductId === 'string' && shopifyProductId.includes('Product/')) {
              const match = shopifyProductId.match(/Product\/(\d+)/);
              if (match && match[1]) {
                productGid = `gid://shopify/Product/${match[1]}`;
              }
            }
          }
          
          // Fallback to product_handle
          if (!productGid && primaryProduct.product_handle) {
            productGid = await getProductGidFromHandle(primaryProduct.product_handle);
          }
        }
      }
      
      // If no product GID found, skip metafield update
      if (!productGid) {
        console.warn('[Home] Could not get product GID for campaign, skipping metafield status update:', campaign.id);
        return;
      }
      
      // Get existing metafield
      const existingMetafield = await getProductMetafield(productGid);
      if (!existingMetafield || !existingMetafield.value) {
        console.warn('[Home] No existing metafield found for product, skipping status update:', productGid);
        return;
      }
      
      // Parse existing metafield JSON
      let campaignData;
      try {
        campaignData = JSON.parse(existingMetafield.value);
      } catch (parseError) {
        console.error('[Home] Error parsing existing metafield JSON:', parseError);
        return;
      }
      
      // Update campaign_status in formValues
      if (campaignData && campaignData.formValues) {
        campaignData.formValues.campaign_status = newStatus;
        
        // Update the metafield with new status
        await updateProductMetafield(productGid, campaignData);
      }
    } catch (error) {
      console.error('[Home] Error updating product metafield status for campaign:', error);
    }
  }, [getProductGidFromHandle]);

  // Perform actual deletion - separated from modal confirmation
  const performDeleteCampaigns = useCallback(async () => {
    if (selectedResources.length === 0) return;

    setUpdatingStatus(true);
    try {
      // Get campaigns being deleted to extract product information
      const campaignsToDelete = displayedCampaigns.filter(campaign => 
        selectedResources.includes(campaign.id)
      );
      
      // Delete product metafields for each campaign before deleting the campaign
      const metafieldDeletePromises = campaignsToDelete.map(campaign => 
        deleteCampaignProductMetafields(campaign)
      );
      
      // Wait for all metafield deletions to complete (don't fail if some fail)
      await Promise.allSettled(metafieldDeletePromises);
      
      // Handle bulk delete
      const response = await campaignAPI.bulkDeleteCampaigns(selectedResources, installData?.shop_info?.id || 1);

      if (response.success) {
        // Clear all selections
        handleSelectionChange('all', false);

        // Reset filter cache to force fresh fetch
        lastFiltersRef.current = null;
        
        // Reset to page 1 if not already there (to avoid empty page after deletion)
        const shouldResetPage = currentPage > 1;
        if (shouldResetPage) {
          setCurrentPage(1);
        }

        // Refresh campaigns list with current filters (always use filtered API)
        // Use page 1 if we reset the page, otherwise use current page
        await loadFilteredCampaigns(shouldResetPage ? 1 : null);

        setToastMessage(`Successfully deleted ${selectedResources.length} campaign(s)`);
        setShowToast(true);
      } else {
        setToastMessage(response.message || 'Failed to delete campaigns');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error deleting campaigns:', error);
      setToastMessage('Failed to delete campaigns');
      setShowToast(true);
    } finally {
      setUpdatingStatus(false);
      // Close modal and reset checkbox
      setShowDeleteModal(false);
      setDeleteConfirmationChecked(false);
    }
  }, [selectedResources, displayedCampaigns, handleSelectionChange, loadFilteredCampaigns, installData, currentPage, deleteCampaignProductMetafields]);

  // Handle bulk status update - memoized
  const handleBulkStatusUpdate = useCallback(async (status) => {
    if (!status || selectedResources.length === 0) return;

    // For Delete action, show confirmation modal first
    if (status === 'Delete') {
      setShowDeleteModal(true);
      setDeleteConfirmationChecked(false); // Reset checkbox when opening modal
      return; // Don't proceed with deletion yet
    }

    setUpdatingStatus(true);
    try {
        // Handle status updates
        let statusValue;
        switch (status) {
          case 'Active':
            statusValue = '1';
            break;
          case 'Draft':
            statusValue = '0';
            break;
          default:
            statusValue = '0';
        }

        // Get campaigns being updated to extract product information
        const campaignsToUpdate = displayedCampaigns.filter(campaign => 
          selectedResources.includes(campaign.id)
        );

        // Update each selected campaign using Redux
        const updatePromises = selectedResources.map(campaignId => {
          return dispatch(updateCampaignStatus({ id: campaignId, status: statusValue, clientId: installData?.shop_info?.id || 1 }));
        });

        await Promise.all(updatePromises);

        // Update product metafields with new status for each campaign
        const metafieldUpdatePromises = campaignsToUpdate.map(campaign => 
          updateCampaignProductMetafieldStatus(campaign, statusValue)
        );
        
        // Wait for all metafield updates to complete (don't fail if some fail)
        await Promise.allSettled(metafieldUpdatePromises);

        // Reset filter cache to force fresh fetch
        lastFiltersRef.current = null;

        // Refresh campaigns list with current filters (always use filtered API)
        await loadFilteredCampaigns();

        // Clear all selections after successful status update
        handleSelectionChange('all', false);

        setToastMessage(`Successfully updated ${selectedResources.length} campaign(s) to ${status}`);
        setShowToast(true);
    } catch (error) {
      console.error('Error updating campaign status:', error);
      setToastMessage(`Failed to ${status.toLowerCase()} campaigns`);
      setShowToast(true);
    } finally {
      setUpdatingStatus(false);
    }
  }, [selectedResources, displayedCampaigns, handleSelectionChange, dispatch, loadFilteredCampaigns, installData, currentPage, deleteCampaignProductMetafields, updateCampaignProductMetafieldStatus]);

  // Handle delete confirmation - only proceed if checkbox is checked
  const handleConfirmDelete = useCallback(() => {
    if (!deleteConfirmationChecked) return; // Don't proceed if checkbox not checked
    performDeleteCampaigns();
  }, [deleteConfirmationChecked, performDeleteCampaigns]);

  // Handle delete cancellation - close modal and reset checkbox
  const handleCancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteConfirmationChecked(false);
  }, []);

  // Bulk actions - memoized to prevent re-renders
  const bulkActions = useMemo(() => [
    {
      content: 'Set to Active',
      onAction: () => handleBulkStatusUpdate('Active'),
      loading: updatingStatus,
    },
    {
      content: 'Set to Draft',
      onAction: () => handleBulkStatusUpdate('Draft'),
      loading: updatingStatus,
    },
    {
      content: 'Delete',
      destructive: true,
      onAction: () => handleBulkStatusUpdate('Delete'),
      loading: updatingStatus,
    },
  ], [handleBulkStatusUpdate, updatingStatus]);
  // Format date for display - memoized
  const formatDate = useCallback((date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date);
  }, []);

  // Handle row click to edit campaign - memoized
  const handleRowClick = useCallback((campaignId) => {
    const path = `/app/campaigns/${campaignId}`;
    // Update parent window URL using App Bridge History
    if (app) {
      try {
        const history = History.create(app);
        history.dispatch(History.Action.PUSH, path);
      } catch (error) {
        console.warn('[Home] App Bridge History failed, using react-router:', error);
      }
    }
    // Update iframe URL using react-router
    navigate(path);
  }, [navigate, app]);

  // Handle create campaign - memoized
  const handleCreateCampaign = useCallback(() => {
    const path = '/app/campaigns/new';
    // Update parent window URL using App Bridge History
    if (app) {
      try {
        const history = History.create(app);
        history.dispatch(History.Action.PUSH, path);
      } catch (error) {
        console.warn('[Home] App Bridge History failed, using react-router:', error);
      }
    }
    // Update iframe URL using react-router
    navigate(path);
  }, [navigate, app]);

  // Row markup for IndexTable - memoized to prevent re-renders
  const rowMarkup = useMemo(() => displayedCampaigns.map((campaign, index) => (
    <IndexTable.Row
      id={campaign.id}
      key={campaign.id}
      selected={selectedResources.includes(campaign.id)}
      position={index}
      onClick={() => handleRowClick(campaign.id)}
    >
      <IndexTable.Cell>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Thumbnail
            source={campaign.image}
            alt={campaign.campaign_name}
            size="small"
          />
          <Text variant="bodyMd" fontWeight="medium">
            {campaign.campaign_name}
          </Text>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={getStatusTone(getCampaignStatus(campaign.campaign_status))}>
          {getCampaignStatus(campaign.campaign_status)}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={getTypeTone(getCampaignType(campaign.campaign_type))}>
          {getCampaignType(campaign.campaign_type)}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text variant="bodyMd">
          {formatDate(campaign.campaign_end_datetime)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end" numeric>
          {(() => {
            const countData = campaign.count_data || {};
            const campaignType = campaign.campaign_type;
            const goalType = campaign.goal_type;
            const isAddStartPoint = campaign.is_add_start_point === '1' || campaign.is_add_start_point === 1;
            
            // Pre-sale: show items sold + initial_items_sold_preSale
            if (campaignType === '0') {
              const itemsSold = countData.items_sold || 0;
              const initialItemsSoldPreSale = parseInt(campaign.initial_items_sold_preSale) || 0;
              return `${itemsSold + initialItemsSoldPreSale}`;
            }
            // Product Drop: show items sold (no initial values for these)
            else if (campaignType === '2') {
              return `${countData.items_sold || 0}`;
            }
            // Crowdfunding: show items or amount based on goal_type, including initial values if addStartPoint is enabled
            else if (campaignType === '1') {
              if (goalType === '0') {
                // Items goal: show items sold + initial backers (if addStartPoint)
                const itemsSold = countData.items_sold || 0;
                const initialBackers = isAddStartPoint ? (parseInt(campaign.initial_backers) || 0) : 0;
                return `${itemsSold + initialBackers}`;
              } else if (goalType === '1') {
                // Amount goal: show amount raised + initial fund (if addStartPoint)
                const amountRaised = parseFloat(countData.amount_raised) || 0;
                const initialFund = isAddStartPoint ? (parseFloat(campaign.initial_fund) || 0) : 0;
                return formatCurrency(amountRaised + initialFund);
              }
            }
            return '0';
          })()}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end" numeric>
          {(() => {
            const countData = campaign.count_data || {};
            const campaignType = campaign.campaign_type;
            const goalType = campaign.goal_type;
            
            // Pre-sale: show items sold + initial_items_sold_preSale
            if (campaignType === '0') {
              const itemsSold = countData.items_sold || 0;
              const initialItemsSoldPreSale = parseInt(campaign.initial_items_sold_preSale) || 0;
              return `${itemsSold + initialItemsSoldPreSale}`;
            }
            // Product Drop: show items sold
            else if (campaignType === '2') {
              return `${countData.items_sold || 0}`;
            }
            // Crowdfunding: show items or amount based on goal_type
            else if (campaignType === '1') {
              if (goalType === '0') {
                // Items goal: show items sold
                return `${countData.items_sold || 0}`;
              } else if (goalType === '1') {
                // Amount goal: show amount raised
                return formatCurrency(countData.amount_raised || 0);
              }
            }
            return '0';
          })()}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end" numeric>
          {campaign.goal_type == '0' ? `${campaign.initial_fund}` : formatCurrency(campaign.initial_fund)}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Text as="span" alignment="end" numeric>
          {(() => {
            const campaignType = campaign.campaign_type;
            
            // Product Drop (type 2): use product_drop_total_limit
            if (campaignType === '2') {
              return campaign.product_drop_total_limit !== null && campaign.product_drop_total_limit !== undefined
                ? `${campaign.product_drop_total_limit}`
                : '0';
            }
            
            // Pre-sale (type 0): use available_inventory
            if (campaignType === '0') {
              return campaign.available_inventory !== null && campaign.available_inventory !== undefined
                ? `${campaign.available_inventory}`
                : '0';
            }
            
            // Crowdfunding (type 1): use current logic based on goal_type
            if (campaignType === '1') {
              return campaign.goal_type === '0' 
                ? `${campaign.number_of_items || 0}` 
                : formatCurrency(campaign.total_amount || 0);
            }
            
            // Fallback: use current logic
            return campaign.goal_type === '0' 
              ? `${campaign.number_of_items || 0}` 
              : formatCurrency(campaign.total_amount || 0);
          })()}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Box width="150px">
          {(() => {
            // Calculate progress once and reuse
            let progressValue = 0;
            
            // Use backend progress if available, otherwise calculate
            if (campaign.progress !== undefined && campaign.progress !== null) {
              progressValue = campaign.progress;
            } else {
              // Calculate progress based on campaign type
              const countData = campaign.count_data || {};
              const campaignType = campaign.campaign_type;
              const goalType = campaign.goal_type;
              
              // Product Drop (type 2): items_sold / product_drop_total_limit
              if (campaignType === '2') {
                const itemsSold = countData.items_sold || 0;
                const totalDropLimit = parseInt(campaign.product_drop_total_limit) || 0;
                if (totalDropLimit > 0) {
                  progressValue = Math.min(Math.round((itemsSold / totalDropLimit) * 100), 100);
                }
              }
              // Pre-sale (type 0): (items_sold + initial_items_sold_preSale) / available_inventory (if inventory is limited)
              else if (campaignType === '0') {
                const itemsSold = countData.items_sold || 0;
                const initialItemsSoldPreSale = parseInt(campaign.initial_items_sold_preSale) || 0;
                const totalItemsSold = itemsSold + initialItemsSoldPreSale;
                const isLimitInventory = campaign.is_limit_inventory === '1' || campaign.is_limit_inventory === 1;
                const availableInventory = parseInt(campaign.available_inventory) || 0;
                if (isLimitInventory && availableInventory > 0) {
                  progressValue = Math.min(Math.round((totalItemsSold / availableInventory) * 100), 100);
                }
              }
              // Crowdfunding (type 1): based on goal_type
              else if (campaignType === '1') {
                const isAddStartPoint = campaign.is_add_start_point === '1' || campaign.is_add_start_point === 1;
                
                if (goalType === '0') {
                  // Items goal: (items_sold + initial_backers) / number_of_items
                  const itemsSold = countData.items_sold || 0;
                  const initialBackers = isAddStartPoint ? (parseInt(campaign.initial_backers) || 0) : 0;
                  const totalItems = parseInt(campaign.number_of_items) || 0;
                  if (totalItems > 0) {
                    progressValue = Math.min(Math.round(((itemsSold + initialBackers) / totalItems) * 100), 100);
                  }
                } else if (goalType === '1') {
                  // Amount goal: (amount_raised + initial_fund) / total_amount
                  const amountRaised = parseFloat(countData.amount_raised) || 0;
                  const initialFund = isAddStartPoint ? (parseFloat(campaign.initial_fund) || 0) : 0;
                  const totalAmount = parseFloat(campaign.total_amount) || 0;
                  if (totalAmount > 0) {
                    progressValue = Math.min(Math.round(((amountRaised + initialFund) / totalAmount) * 100), 100);
                  }
                }
              }
            }
            
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Box width="100px">
                  <ProgressBar progress={progressValue} size="small" />
                </Box>
                <Text variant="bodyMd" tone="subdued">
                  {progressValue}%
                </Text>
              </div>
            );
          })()}
        </Box>
      </IndexTable.Cell>
    </IndexTable.Row>
  )), [displayedCampaigns, selectedResources, formatDate, formatCurrency, getCampaignStatus, getCampaignType, getStatusTone, getTypeTone, handleRowClick]);


  // Handle preview click to complete step 3 - directly update onboarding status to 3 and redirect to first campaign product
  // Handle preview click - only redirects to product page
  const handlePreviewClick = useCallback(async () => {
    if (!installChecked || !installData) {
      setToastMessage('Please wait for app installation check to complete');
      setShowToast(true);
      return;
    }

    // Check if there are campaigns with product info
    if (!hasCampaigns || campaigns.length === 0) {
      setToastMessage('Please create a campaign first before previewing');
      setShowToast(true);
      return;
    }

    // Get the first campaign with product handle
    const firstCampaign = campaigns.find(c => c.product_handle) || campaigns[0];
    
    if (!firstCampaign || !firstCampaign.product_handle) {
      setToastMessage('No product found for preview. Please ensure your campaign has a product.');
      setShowToast(true);
      return;
    }

    // Generate product preview URL
    const shopName = shop?.includes('.myshopify.com') 
      ? shop.replace('.myshopify.com', '') 
      : shop;
    
    if (!shopName) {
      setToastMessage('Shop information not available');
      setShowToast(true);
      return;
    }

    const productUrl = `https://${shopName}.myshopify.com/products/${firstCampaign.product_handle}`;
    
    // Try App Bridge redirect first
    if (app) {
      try {
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, {
          url: productUrl,
          newContext: true
        });
      } catch (error) {
        console.warn('[Home] App Bridge redirect failed, using window.open:', error);
        window.open(productUrl, '_blank');
      }
    } else {
      // Fallback: open in new tab
      window.open(productUrl, '_blank');
    }
    
    // Show success message
    setToastMessage('Redirecting to product page...');
    setShowToast(true);
    
  }, [installChecked, installData, hasCampaigns, campaigns, shop, app]);

  // Handle complete setup - updates onboarding status to 3 in both backend and Redux
  // This is Step 5: Confirm campaigns are working properly
  const handleCompleteSetup = useCallback(async () => {
    if (!installChecked || !installData) {
      setToastMessage('Please wait for app installation check to complete');
      setShowToast(true);
      return;
    }

    const clientId = installData?.shop_info?.id;
    if (!clientId) {
      setToastMessage('Unable to update onboarding status: Client ID not found');
      setShowToast(true);
      return;
    }

    try {
      // Update onboarding status to 3 in backend (Step 5: Complete Setup)
      const response = await clientAPI.updateOnboardingStatus(clientId, '3');
      
      if (response.success) {
        // Update Redux store with new onboarding status (source of truth)
        dispatch(updateOnboardingStatus({ onboardingStatus: '3' }));
        
        // Update local state
        setStep3Completed(true);
        lastOnboardingStatusSentRef.current = '3';
        
        // Show success message
        setToastMessage('Setup completed successfully!');
        setShowToast(true);
      } else {
        throw new Error(response.message || 'Failed to update onboarding status');
      }
    } catch (error) {
      console.error('[Home] Failed to update onboarding status on complete setup:', error);
      setToastMessage('Failed to complete setup. Please try again.');
      setShowToast(true);
    }
  }, [installChecked, installData, dispatch]);

  // Generate theme editor URL
  const getThemeEditorUrl = useCallback(() => {
    if (!shop) {
      console.warn('[Home] Cannot generate theme editor URL: shop missing', { shop });
      return null;
    }
    
    // Extract shop name from shop domain (e.g., "myshop.myshopify.com" -> "myshop")
    // Handle both formats: "myshop.myshopify.com" or "myshop"
    const shopName = shop.includes('.myshopify.com') 
      ? shop.replace('.myshopify.com', '') 
      : shop;
    
    // Get app ID from config
    const appId = SHOPIFY_API_KEY;
    
    if (!appId) {
      console.warn('[Home] Cannot generate theme editor URL: app ID missing');
      return null;
    }
    
    // Generate store-specific URL with app activation
    // Format: https://{shop-name}.myshopify.com/admin/themes/current/editor?context=apps&activateAppId={appId}/campaign
    return `https://${shopName}.myshopify.com/admin/themes/current/editor?context=apps&activateAppId=${appId}/campaign`;
  }, [shop]);

  // Handle redirect to theme editor - refresh theme status after redirect
  const handleOpenThemeEditor = useCallback(async () => {
    // Don't redirect if block is already active
    if (!appBlockDisabled) {
      return;
    }
    
    const themeEditorUrl = getThemeEditorUrl();
    if (!themeEditorUrl) {
      console.error('[Home] Failed to generate theme editor URL');
      return;
    }
    
    // Try App Bridge redirect first
    if (app) {
      try {
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, {
          url: themeEditorUrl,
          newContext: true
        });
        
        // Refresh theme status after a delay to check if block was enabled
        setTimeout(() => {
          themesLoadedRef.current = false;
          fetchStoreThemes(true);
        }, 2000);
        return;
      } catch (error) {
        console.warn('[Home] App Bridge redirect failed, using window.open:', error);
      }
    }
    // Fallback: open in new tab
    window.open(themeEditorUrl, '_blank');
    
    // Refresh theme status after a delay to check if block was enabled
    themesLoadedRef.current = false;
    fetchStoreThemes(true);
  }, [getThemeEditorUrl, app, appBlockDisabled, fetchStoreThemes]);

  const getThemeEditorUrlBlcok = useCallback((blockHandle, target = 'mainSection', template = 'product') => {
    if (!shop) {
      console.warn('[Widget] Cannot generate theme editor URL: shop missing', { shop });
      return null;
    }
    
    // Extract shop name from shop domain (e.g., "myshop.myshopify.com" -> "myshop")
    // Handle both formats: "myshop.myshopify.com" or "myshop"
    const shopName = shop.includes('.myshopify.com') 
      ? shop.replace('.myshopify.com', '') 
      : shop;
    
    // Get app API key from config
    const apiKey = SHOPIFY_API_KEY;
    
    if (!apiKey) {
      console.warn('[Widget] Cannot generate theme editor URL: app API key missing');
      return null;
    }
    
    if (!blockHandle) {
      console.warn('[Widget] Cannot generate theme editor URL: block handle missing');
      return null;
    }
    
    // Generate deep linking URL for app blocks
    // Format: https://{shop-name}.myshopify.com/admin/themes/current/editor?addAppBlockId={api_key}/{handle}&target={target}&template={template}
    return `https://${shopName}.myshopify.com/admin/themes/current/editor?addAppBlockId=${apiKey}/${blockHandle}&target=${target}&template=${template}`;
  }, [shop]);

  // Handle redirect to theme editor - Dynamic handler for any widget
  // Updates onboarding status to 4 when widget block is added (Step 2)
  const handleOpenThemeEditorBlock = useCallback(async () => {
    // Generate theme editor URL
    const themeEditorUrl = getThemeEditorUrlBlcok('campaign_main');
    if (!themeEditorUrl) {
      console.error('[Widget] Failed to generate theme editor URL');
      return;
    }
    
    const clientId = installData?.shop_info?.id;
    if (!clientId) {
      console.error('[Home] Cannot update onboarding status: Client ID not found');
      return;
    }
    
    // Update local state immediately for UI feedback
    setStep4Completed(true);
    
    // Update backend and Redux to status 4 (Step 2: Widget block added)
    // Only update if current status is less than 3 (don't override complete setup)
    const currentStatus = parseInt(onboardingStatusFromBackend);
    if (currentStatus < 3) {
      try {
        const response = await clientAPI.updateOnboardingStatus(clientId, '4');
        if (response.success) {
          // Update Redux store with new onboarding status
          dispatch(updateOnboardingStatus({ onboardingStatus: '4' }));
          lastOnboardingStatusSentRef.current = '4';
        } else {
          throw new Error(response.message || 'Failed to update onboarding status');
        }
      } catch (error) {
        console.error('[Home] Failed to update onboarding status to 4:', error);
        // Reset local state on error
        setStep4Completed(false);
      }
    }
    
    // Try App Bridge redirect first
    if (app) {
      try {
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, {
          url: themeEditorUrl,
          newContext: true
        });
        return;
      } catch (error) {
        console.warn('[Widget] App Bridge redirect failed, using window.open:', error);
      }
    }
    
    
    // Fallback: open in new tab
    window.open(themeEditorUrl, '_blank');
  }, [getThemeEditorUrlBlcok, app, installData, onboardingStatusFromBackend, dispatch]);
  
  // Handle redirect to checkout settings - Updates onboarding status to 5 when checkout rules are added (Step 4)
  const handleOpenCheckoutSettings = useCallback(async () => {
    if (!shop) {
      console.warn('[Home] Cannot generate checkout settings URL: shop missing', { shop });
      setToastMessage('Shop information not available');
      setShowToast(true);
      return;
    }
    
    // Extract shop name from shop domain (e.g., "myshop.myshopify.com" -> "myshop")
    // Handle both formats: "myshop.myshopify.com" or "myshop"
    const shopName = shop.includes('.myshopify.com') 
      ? shop.replace('.myshopify.com', '') 
      : shop;
    
    // Generate checkout settings URL
    // Format: https://admin.shopify.com/store/{shop-name}/settings/checkout
    const checkoutSettingsUrl = `https://${shopName}.myshopify.com/admin/settings/checkout/rules/validation/add/019b64a1-9bd6-7d52-87d5-67c6b7c72391`;
    
    const clientId = installData?.shop_info?.id;
    if (!clientId) {
      console.error('[Home] Cannot update onboarding status: Client ID not found');
      return;
    }
    try {
      const response = await clientAPI.updateOnboardingStatus(clientId, '5');
      if (response.success) {
        // Update Redux store with new onboarding status
        dispatch(updateOnboardingStatus({ onboardingStatus: '5' }));
        lastOnboardingStatusSentRef.current = '5';
        // Update local state after successful backend update
        setStep5Completed(true);
      } else {
        throw new Error(response.message || 'Failed to update onboarding status');
      }
    } catch (error) {
      console.error('[Home] Failed to update onboarding status to 5:', error);
      // Don't update local state on error - let it be determined by backend status
    }

    setStep5Completed(true);
    
    // Try App Bridge redirect first
    if (app) {
      try {
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, {
          url: checkoutSettingsUrl,
          newContext: true
        });
        return;
      } catch (error) {
        console.warn('[Home] App Bridge redirect failed, using window.open:', error);
      }
    }
    
    // Fallback: open in new tab
    window.open(checkoutSettingsUrl, '_blank');
  }, [app, shop, installData, onboardingStatusFromBackend, dispatch]);

  const handleRequestBillingApproval = useCallback(async () => {
    const clientId = installData?.shop_info?.id;
    if (!shop || !clientId) {
      setToastMessage('Shop or client information is missing. Try reloading the app.');
      setShowToast(true);
      return;
    }
    setBillingApprovalLoading(true);
    try {
      const res = await apiCall(
        'billing/request-charge',
        'POST',
        { shop, client_id: clientId, host: host || undefined },
        app,
      );
      const confirmationUrl = res?.data?.confirmationUrl;
      if (!res?.success || !confirmationUrl) {
        throw new Error(res?.message || 'Could not start billing approval');
      }
      if (app) {
        try {
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, {
            url: confirmationUrl,
            newContext: false,
          });
          return;
        } catch (redirectErr) {
          console.warn('[Home] Billing redirect failed:', redirectErr);
        }
      }
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = confirmationUrl;
        } else {
          window.location.href = confirmationUrl;
        }
      } catch {
        window.location.assign(confirmationUrl);
      }
    } catch (err) {
      const msg =
        typeof err === 'object' && err !== null && 'message' in err
          ? err.message
          : String(err);
      setToastMessage(
        msg ||
          'Could not open Shopify billing. If you already have an active subscription, return to the app to finish setup.',
      );
      setShowToast(true);
    } finally {
      setBillingApprovalLoading(false);
    }
  }, [shop, host, installData, app]);

  // Steps configuration - memoized with dynamic status (billing step omitted for legacy user_type === '0')
  const steps = useMemo(() => {
    const base = [
    {
      title: "Create a Campaign",
      description:
        `Choose which products you want to include in your campaign.
You can select the campaign type: Crowdfunding, PreSale, or Product Drop.
Each type lets you configure different rules such as goals, payment timing, and inventory logic.`,
      buttonLabel: "Create campaign",
      onClick: handleCreateCampaign,
      // Step 1 is completed when campaigns exist (checked dynamically)
      completed: hasCampaigns,
    },
    {
      title: "Add widget block on product page",
      description: "Add the widget block in your product page so customers can see campaign details and buttons.",
      buttonLabel: "Add block",
      onClick: handleOpenThemeEditorBlock,
      // Step 2 is completed when widget block is added
      completed: step4Completed,
    },
    {
      title: "Activate app embed in Shopify",
      description: themeLoading 
        ? "Loading theme information..." 
        : themeId 
          ? "Enable the campaign widget in your storefront so customers can see campaign details and buttons."
          : "Unable to load theme information. Please refresh the page.",
      buttonLabel: themeLoading ? "Loading..." : (!appBlockDisabled ? "Enabled" : "Enable"),
      onClick: handleOpenThemeEditor,
      disabled: !themeId || themeLoading || !appBlockDisabled, // Disable if already enabled (block is active)
      // Step 3 is completed only when theme block is active (always check theme status)
      completed: !appBlockDisabled,
    },
    {
      title: "Add checkout rules in settings",
      description: "Save and turn on 'Checkout rule for product-validation' to ensure proper order processing for your campaigns.",
      buttonLabel: "Add rule",
      onClick: handleOpenCheckoutSettings,
      // Step 4 is completed when checkout rules are added
      completed: step5Completed,
    },
    {
      title: "Confirm campaigns are working properly",
      description: "Click to confirm that your campaigns are working properly and complete the setup.",
      buttons: [
        {
          label: step3Completed ? "Completed" : "Complete Setup",
          onClick: handleCompleteSetup,
          disabled: step3Completed,
          tooltip: "Complete the setup to mark this step as done"
        },
        {
          label: "Preview",
          onClick: handlePreviewClick,
          tooltip: "Preview your campaign on the product page"
        }
      ],
      // Step 5 is completed when onboarding status is updated to 3
      completed: step3Completed,
    },
    ];
    if (showBillingSetupStep) {
      base.push({
        title: 'Approve Pro Plan billing (usage charges)',
        description:
          'Click to open Shopify and approve the Pro Plan. We charge 2% of your campaign product order revenue (USD). After you approve, you will return to the app and this step completes automatically.',
        notice: billingApprovalNotice || {
          tone: 'info',
          title: '2% usage fee',
          body: 'Orders from your campaigns are billed at 2% of product revenue in USD.',
        },
        buttons: [
          {
            label: step6Completed
              ? 'Completed'
              : billingApprovalLoading
                ? 'Opening…'
                : 'Activate Now',
            onClick: handleRequestBillingApproval,
            disabled: step6Completed || billingApprovalLoading,
            tooltip:
              'Starts appSubscriptionCreate (Pro Plan) and opens the Shopify confirmation page',
          },
        ],
        completed: step6Completed,
      });
    }
    return base;
  }, [
    handleCreateCampaign,
    handleCompleteSetup,
    handlePreviewClick,
    handleRequestBillingApproval,
    handleOpenThemeEditor,
    handleOpenThemeEditorBlock,
    handleOpenCheckoutSettings,
    themeId,
    themeLoading,
    hasCampaigns,
    appBlockDisabled,
    step3Completed,
    step4Completed,
    step5Completed,
    step6Completed,
    billingApprovalLoading,
    billingApprovalNotice,
    showBillingSetupStep,
  ]);

  const progress = useMemo(
    () => (completedSteps / totalSetupSteps) * 100,
    [completedSteps, totalSetupSteps],
  );

  /** DB `clients.is_charge_approved`. Legacy (`user_type === '0'`) never counts as approved — billing step is hidden. */
  const isChargeApproved = useMemo(
    () =>
      !isLegacyCustomer &&
      String(installData?.shop_info?.is_charge_approved) === '1',
    [installData, isLegacyCustomer],
  );

  // Billing: hide when Complete Setup (is_on_boarding 3) AND charge approved.
  // Legacy (user_type 0): no billing step — hide when DB says Complete Setup (3), not only local step counts.
  const showSetupGuide = useMemo(() => {
    if (showBillingSetupStep) {
      return !(completeSetupDone && isChargeApproved);
    }
    if (completeSetupDone) {
      return false;
    }
    return completedSteps < totalSetupSteps;
  }, [
    showBillingSetupStep,
    completeSetupDone,
    isChargeApproved,
    completedSteps,
    totalSetupSteps,
  ]);

  // OPTIMIZATION: Removed periodic refresh - only refresh manually via refresh button
  // This prevents unnecessary API calls during initial load
  
  
  // Show Theme Extension card when Complete Setup is saved (is_on_boarding === '3')
  const showThemeExtensionCard = useMemo(() => {
    return completeSetupDone;
  }, [completeSetupDone]);

  const tabs = useMemo(() => TAB_STRINGS.map((item, index) => ({
    content: item,
    index,
    onAction: () => handleTabChange(index),
    id: `${item}-${index}`,
    isLocked: index === 0,
  })), [handleTabChange]);

  useEffect(() => {
    if (document.querySelectorAll('.Polaris-Filters__Container .Polaris-Button.Polaris-Button--pressable').length) {
      document.querySelector('.Polaris-Filters__Container .Polaris-Button.Polaris-Button--pressable').textContent = 'Cancel'; 
    }
  });

  return (
    <>
      <PageTitleBar 
        title="Home"
        subtitle="Campaign Management Dashboard"
      />
      <Page>
        <BlockStack>
          {/* Onboarding Section - Hide when all steps are completed */}
          {showSetupGuide && (
            <Box paddingBlockEnd="400">
              <Card sectioned className="preorder-card">
              <BlockStack gap="0">
                {/* Header Section */}
                <div className="preorder-setup-header">
                  <div>
                    <Text as="h3" variant="headingMd" className="preorder-title">
                      Setup Guide
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued" className="preorder-subtitle">
                      Follow these steps to start using Campaigns
                    </Text>
                  </div>

                  <div className="preorder-header-actions">
                    <ButtonGroup>
                      <Tooltip content={isCollapsed ? "Expand setup guide" : "Collapse setup guide"}>
                        <Button
                          icon={isCollapsed ? ChevronDownIcon : ChevronUpIcon}
                          variant="tertiary"
                          size="small"
                          onClick={() => setIsCollapsed(!isCollapsed)}
                        />
                      </Tooltip>
                    </ButtonGroup>
                  </div>
                </div>

                {/* Progress Section */}
                <div className="preorder-progress-area">
                  <div className="preorder-progress-left">
                    <Text as="p" variant="bodyMd" className="preorder-progress-text">
                      {completedSteps} / {totalSetupSteps} completed
                    </Text>
                  </div>
                  <div className="preorder-progress-right">
                    <ProgressBar progress={progress} size="small" />
                  </div>
                </div>

                {/* Steps Section */}
                {!isCollapsed && (
                  <div className="preorder-steps">
                    {steps.map((step, index) => {
                      // Check individual step completion status
                      const stepKey = `step${index + 1}`;
                      const isCompleted = stepCompletionStatus[stepKey] || false;
                      const isExpanded = expandedStep === stepKey;
                      
                      // Find the first incomplete step to highlight as current
                      const firstIncompleteIndex = steps.findIndex((_, idx) => {
                        const key = `step${idx + 1}`;
                        return !stepCompletionStatus[key];
                      });
                      const isCurrentStep = index === (firstIncompleteIndex === -1 ? steps.length : firstIncompleteIndex);

                      return (
                        <div
                          key={index}
                          className={`preorder-step ${isCurrentStep ? "preorder-step--active" : ""}`}
                          onClick={() => openStep(stepKey)}
                          style={{ cursor: 'pointer' }}
                        >
                          {/* Left section - Icon */}
                          <div className="preorder-step-left">
                            <div 
                              className={`preorder-step-icon ${isCompleted ? 'completed' : 'pending'}`}
                            >
                              {isCompleted ? (
                                <Icon source={CheckCircleIcon} tone="success" />
                              ) : (
                                <div className="dashed-circle"></div>
                              )}
                            </div>
                          </div>

                          {/* Right section - Title, Description, Button */}
                          <div className="preorder-step-right" style={{ flex: 1 }}>
                            <Text
                              as="h3"
                              variant={isExpanded ? "headingMd" : "bodyLg"}
                              className="preorder-step-title"
                            >
                              {step.title}
                            </Text>

                            {/* Show description and button when step is expanded */}
                            {isExpanded && (
                              <div className="preorder-step-details" onClick={(e) => e.stopPropagation()}>
                                {step.description && (
                                  <Text as="p" variant="bodyMd" tone="subdued" className="preorder-step-desc">
                                    {step.description}
                                  </Text>
                                )}
                                {step.notice && (
                                  <Box paddingBlockStart="300" paddingBlockEnd="200">
                                    <Banner tone={step.notice.tone} title={step.notice.title}>
                                      <Text as="p" variant="bodySm">
                                        {step.notice.body}
                                      </Text>
                                    </Banner>
                                  </Box>
                                )}

                                {step.buttons ? (
                                  <div className="preorder-step-action" style={{ display: 'flex', gap: '8px' }}>
                                    {step.buttons.map((button, btnIndex) => (
                                      <Tooltip key={btnIndex} content={button.tooltip}>
                                        <Button 
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            button.onClick();
                                          }} 
                                          variant="primary" 
                                          size="slim"
                                          disabled={button.disabled}
                                        >
                                          {button.label}
                                        </Button>
                                      </Tooltip>
                                    ))}
                                  </div>
                                ) : step.buttonLabel && (
                                  <div className="preorder-step-action">
                                    <Tooltip content={
                                      step.title === "Create a Campaign" 
                                        ? "Start by creating your first campaign with products, goals, and settings"
                                        : step.title === "Add checkout rules in settings"
                                        ? "Navigate to Settings > Checkout, add a rule, select 'product-validation' app, save, and enable 'Validate product quantity in online store'"
                                        : step.title === "Activate app embed in Shopify"
                                        ? "Enable the campaign widget in your Shopify theme editor"
                                        : "Preview your campaign on the product page to verify it's working correctly"
                                    }>
                                      <Button 
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          step.onClick();
                                        }} 
                                        variant="primary" 
                                        size="slim"
                                        disabled={step.disabled}
                                      >
                                        {step.buttonLabel}
                                      </Button>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </BlockStack>
            </Card>
            </Box>
          )}

          {/* Theme Extension Card - Show when onboarding status is 3 */}
          {showThemeExtensionCard && (
            <ThemeExtensionCard
              appBlockDisabled={appBlockDisabled}
              themeId={themeId}
              themeLoading={themeLoading}
              onRefresh={handleRefreshTheme}
              onEnable={handleOpenThemeEditor}
            />
          )}

          {/* Campaigns Header */}
          <Box>
            <BlockStack gap="300">
              <InlineStack gap="300" align="space-between" blockAlign="center">
                <BlockStack>
                  <Text variant="headingLg" as="h2" fontWeight="semibold">
                    Campaigns
                  </Text>
                  {/* <Text variant="bodyMd" tone="subdued">
                    Manage your crowdfunding, presale and product drop campaigns
                  </Text> */}
                </BlockStack>
                <Tooltip content="Create a new crowdfunding, presale, or product drop campaign">
                  <Button variant="primary" onClick={handleCreateCampaign}>
                    Create Campaign
                  </Button>
                </Tooltip>
              </InlineStack>
            </BlockStack>
          </Box>

          {/* Campaigns Listing Section */}
          <Box paddingBlockStart="400">
            <LegacyCard>
              <IndexFilters
                sortOptions={sortOptions}
                sortSelected={sortSelected}
                queryValue={queryValue}
                queryPlaceholder="Search campaigns..."
                onQueryChange={handleQueryChange}
                cancelAction={
                  {
                    onAction: () => {
                      handleFiltersClearAll();
                    }
                  }
                }
                onQueryClear={async () => {
                  setCurrentPage(1); // Reset to page 1 when clearing query
                  // Always use filtered API, even when clearing query
                  setFilters({ queryValue: '', hasFiltersApplied: true });
                }}
                onSort={handleSortChange}
                tabs={tabs}
                selected={selected}
                onSelect={handleTabChange}
                canCreateNewView={false}
                filters={filterDefinitions}
                appliedFilters={appliedFilters}
                onClearAll={handleFiltersClearAll}
                mode={mode}
                setMode={setMode}
              />
              
              {/* Loading indicator beside search bar - Polaris pattern */}
              {tableLoading && (
                <Box paddingBlockStart="200" paddingBlockEnd="200" paddingInlineStart="400">
                  <InlineStack gap="200" align="start" blockAlign="center">
                    <Spinner size="small" />
                    <Text variant="bodySm" tone="subdued">
                      Loading campaigns...
                    </Text>
                  </InlineStack>
                </Box>
              )}
              
              <IndexTable
                 condensed={breakpoints.smDown}
                 resourceName={resourceName}
                 itemCount={displayedCampaigns.length}
                 selectedItemsCount={
                   allResourcesSelected ? 'All' : selectedResources.length
                 }
                 onSelectionChange={handleSelectionChange}
                 headings={tableHeadings}
                 bulkActions={bulkActions}
                 loading={tableLoading}
                 emptyState={
                   !tableLoading && displayedCampaigns.length === 0 ? (
                     <Box padding="600" textAlign="center">
                      <div style={{ display: 'flex', justifyContent: 'center'}}>
                       <BlockStack gap="300" align="center">
                         <Text variant="bodyMd" tone="subdued">
                           No campaigns found
                         </Text>
                       </BlockStack>
                       </div>
                     </Box>
                   ) : undefined
                 }
               >
                 {rowMarkup}
               </IndexTable>
               
               {/* Pagination - Show when pagination info is available, totalPages > 1, and not loading */}
               {pagination && pagination.totalPages > 1 && !tableLoading && (
                 <Box padding="400">
                   <Pagination
                     label={`Page ${pagination.page} of ${pagination.totalPages}`}
                     hasPrevious={pagination.hasPreviousPage}
                     onPrevious={() => handlePaginationChange(pagination.page - 1)}
                     hasNext={pagination.hasNextPage}
                     onNext={() => handlePaginationChange(pagination.page + 1)}
                   />
                 </Box>
               )}
            </LegacyCard>
          </Box>
        </BlockStack>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <Modal
            open={showDeleteModal}
            onClose={handleCancelDelete}
            title={`Delete ${selectedResources.length} ${selectedResources.length === 1 ? 'campaign' : 'campaigns'}?`}
            primaryAction={{
              content: selectedResources.length === 1 ? 'Delete campaign' : 'Delete campaigns',
              onAction: handleConfirmDelete,
              destructive: true,
              disabled: !deleteConfirmationChecked || updatingStatus,
              loading: updatingStatus,
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: handleCancelDelete,
                disabled: updatingStatus,
              },
            ]}
          >
            <Modal.Section>
              <BlockStack gap="400">
                <Text as="p" variant="bodyMd">
                This action will permanently delete the selected campaigns and all associated data, including products, orders, analytics, and settings. This action cannot be undone.
                </Text>
                <Checkbox
                  label="I understand"
                  checked={deleteConfirmationChecked}
                  onChange={setDeleteConfirmationChecked}
                  disabled={updatingStatus}
                />
              </BlockStack>
            </Modal.Section>
          </Modal>
        )}

        {showToast && (
          <Toast
            content={toastMessage}
            onDismiss={() => setShowToast(false)}
          />
        )}
      </Page>
    </>
  );
}

export default function Home() {
  const {
    installChecked,
    installSuccess,
    installLoading,
    installData,
  } = useEnsureInstall();

  // Install failed — redirect is in progress; avoid flashing content
  if (installChecked && !installSuccess && !installData) {
    return null;
  }

  // Install check still running (checked stays false until fulfilled/rejected)
  if (!installChecked || (!installData && installLoading)) {
    return (
      <Page narrowWidth title="">
        <Box paddingBlockStart="800">
          <InlineStack align="center" blockAlign="center">
            <Spinner accessibilityLabel="Loading app" size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  return <HomeContent />;
}

